import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authAPI } from '../../api/auth'
import { clearSession } from '../../api/client'
import { getErrorMessage } from '../../utils/apiErrors'

/** Stores a session (sign-in, or the fresh one after a password change). */
export const storeSession = (response) => {
  const user = { userId: response.userId, email: response.email }
  localStorage.setItem('token', response.accessToken)
  localStorage.setItem('refreshToken', response.refreshToken)
  localStorage.setItem('user', JSON.stringify(user))
  return user
}

/** Signs out on the server too (the refresh token stops working), then clears this browser. */
export const signOut = createAsyncThunk('auth/signOut', async () => {
  const refreshToken = localStorage.getItem('refreshToken')
  if (refreshToken) await authAPI.logout(refreshToken)
})

export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(email, password)
      const user = storeSession(response)
      return { user, token: response.accessToken }
    } catch (error) {
      return rejectWithValue(
        error.response?.status === 401 ? 'Invalid email or password' : getErrorMessage(error, 'Login failed')
      )
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    token: localStorage.getItem('token'),
    isAuthenticated: !!localStorage.getItem('token'),
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    logout: (state) => {
      clearSession()
      state.user = null
      state.token = null
      state.isAuthenticated = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signOut.fulfilled, (state) => {
        clearSession()
        state.user = null
        state.token = null
        state.isAuthenticated = false
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false
        state.isAuthenticated = true
        state.user = action.payload.user
        state.token = action.payload.token
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
      })
  },
})

export const { clearError, logout } = authSlice.actions
export default authSlice.reducer
